import { validIcons } from '@brahma/components';
import type { DashboardNavItem, Sublink } from '@dfg/brahma-types';
import { ApplicantPortalOption } from '$constants/applicantPortalOption';
import type { TFunction } from 'i18next';
import { DEFAULT_LNG } from '$locale/constants';
import type { OptionVisibilityState } from '$context/global-context/reducers/types';
import { resolveOptionForUrl } from '$constants/optionVisibility';
import { isOptionVisible } from '$utils/option-visibility';

/** Pre-fetch fallback only. MUST stay key-for-key identical to garuda's map (§4.3.2). */
const OPTION_BY_NAV_ID: ReadonlyMap<string, ApplicantPortalOption> = new Map([
  ['services', ApplicantPortalOption.ANCILLARY_SERVICES],
  ['trueProfileWallet', ApplicantPortalOption.TRUEPROFILE_WALLET],
  ['settings', ApplicantPortalOption.SETTINGS],
  ['support', ApplicantPortalOption.SUPPORT],
  ['chatbot', ApplicantPortalOption.SUPPORT_CHATBOT]
]);

/**
 * Route-keyed companion to `OPTION_BY_NAV_ID`, for the hard-coded fallback list
 * in `getDefaultDashboardNavItems` — those items are keyed by route, not by
 * garuda nav id, so `OPTION_BY_NAV_ID` alone never matches them. Pre-fetch
 * fallback only; MUST stay in agreement with garuda's canonical
 * `filterSidePanelOptions` (§4.3.2).
 *
 * Deliberately absent:
 * - `/dashboard/home` — the post-login landing page and both route guards'
 *   redirect target, so it can never be hidden.
 * - `/dashboard/verification` — the flat fallback tab has no sublinks, so
 *   garuda's parent-retention rule cannot be reproduced here. garuda keeps the
 *   `verification` item whenever its `select-country` / `pqrSelection` sublink
 *   survives, so mapping this route to `CHECK_VERIFICATION_STATUS` would drop a
 *   tab garuda still returns.
 */
const OPTION_BY_NAV_ROUTE: ReadonlyMap<string, ApplicantPortalOption> = new Map(
  [
    ['/dashboard/services', ApplicantPortalOption.ANCILLARY_SERVICES],
    ['/dashboard/vault', ApplicantPortalOption.TRUEPROFILE_WALLET],
    ['/dashboard/manage-account', ApplicantPortalOption.SETTINGS]
  ]
);

/** Prefix, so the co-brand `pqrSelection` rewrite still loses its sublink. */
const OPTION_BY_SUBLINK_PREFIX: ReadonlyArray<
  readonly [string, ApplicantPortalOption]
> = [
  [
    '/verification/select-country',
    ApplicantPortalOption.START_NEW_VERIFICATION
  ],
  ['/verification/pqrSelection', ApplicantPortalOption.START_NEW_VERIFICATION],
  ['/dashboard/verification', ApplicantPortalOption.CHECK_VERIFICATION_STATUS],
  // The `Get Assistance` sublink garuda injects into the `verification` tab
  // keeps its `/dashboard/services` id, so it is only reachable by prefix here.
  ['/dashboard/services', ApplicantPortalOption.ANCILLARY_SERVICES]
];

/** `home` is absent from the map by design, so it can never be hidden. */
const isNavOptionVisible = (
  navId: string,
  visibility: OptionVisibilityState | undefined
): boolean => {
  const option = OPTION_BY_NAV_ID.get(navId);
  return !option || isOptionVisible(option, visibility);
};

/**
 * Fallback-list variant of `isNavOptionVisible`. The hard-coded items are keyed
 * by route, except the id-keyed `support` entry, so both maps are consulted.
 * The two key spaces are disjoint, so the order of lookup is immaterial.
 */
const isFallbackNavOptionVisible = (
  navKey: string,
  visibility: OptionVisibilityState | undefined
): boolean => {
  const option =
    OPTION_BY_NAV_ROUTE.get(navKey) ?? OPTION_BY_NAV_ID.get(navKey);
  return !option || isOptionVisible(option, visibility);
};

const filterVisibleSublinks = (
  sublinks: Sublink[] | undefined,
  visibility: OptionVisibilityState | undefined
): Sublink[] =>
  (sublinks ?? []).filter((sublink: Sublink) => {
    const match = OPTION_BY_SUBLINK_PREFIX.find(([prefix]) =>
      sublink.id.startsWith(prefix)
    );
    return !match || isOptionVisible(match[1], visibility);
  });

export const getDashboardNavItem = (
  item: DashboardNavItem,
  t: TFunction,
  isMobile?: boolean,
  visibility?: OptionVisibilityState
): DashboardNavItem | null => {
  if (!isNavOptionVisible(item.id, visibility)) return null;

  switch (item.id) {
    case 'home':
      return {
        id: '/dashboard/home',
        tabName: t('layout.home'),
        activeIconName: validIcons.home02Custom,
        passiveIconName: validIcons.passiveHome
      };
    case 'verification': {
      const visibleSublinks = filterVisibleSublinks(item.sublinks, visibility);
      // Parent retention mirrors garuda's `filterSidePanelOptions`: a parent
      // that had sublinks and now has none is dropped with them.
      if (item.sublinks?.length && !visibleSublinks.length) return null;

      return {
        id: '/dashboard/verification',
        tabName: item.tabName, // Use backend translation
        activeIconName: validIcons.checkVerified02Custom,
        passiveIconName: validIcons.passiveCheckVerified,
        sublinks: visibleSublinks.map((sublink: Sublink) => {
          return {
            id: `${sublink.id}`,
            tabName: sublink.tabName // Use backend translation directly
          };
        }),
        isNonLink: !isMobile
      };
    }
    case 'services':
      return {
        id: '/dashboard/services',
        tabName: item.tabName, // Use backend translation
        activeIconName: validIcons.grid01Custom,
        passiveIconName: validIcons.passiveGrid,
        sublinks: item?.sublinks
          ? item.sublinks.map((sublink: Sublink) => ({
              id: `/dashboard/services/${sublink.id}`,
              tabName: sublink.tabName
            }))
          : [],
        isNonLink: false
      };
    case 'trueProfileWallet':
      return {
        id: '/dashboard/vault',
        tabName: item.tabName, // Use backend translation
        activeIconName: validIcons.TPIcon,
        passiveIconName: validIcons.TPIcon
      };
    case 'settings':
      return {
        id: '/dashboard/manage-account',
        tabName: t('layout.settings'),
        activeIconName: validIcons.setting01Custom,
        passiveIconName: validIcons.passiveSetting
      };
    case 'chatbot':
      return isMobile
        ? {
            id: 'chatbot',
            tabName: item.tabName,
            activeIconName: validIcons.messageDotsCircle,
            passiveIconName: validIcons.messageDotsCircle,
            isNonLink: true
          }
        : null;
    case 'support':
      return {
        id: 'support',
        tabName: item.tabName, // Use backend translation
        activeIconName: validIcons.messageChatPrimary,
        passiveIconName: validIcons.messageChatNormal,
        isNonLink: true
      };
    default: {
      if (item?.tabName === 'Job Verification' && isMobile) {
        return null;
      }

      /* Banner nav items land here, and their `id` is the banner's own
         config-driven target URL rather than a garuda nav id, so neither
         `OPTION_BY_NAV_ID` nor `OPTION_BY_SUBLINK_PREFIX` can reach them.
         Resolved by path instead, through the same rules the route guard uses.

         Filtered portal-side rather than in garuda's `filterSidePanelOptions`
         because `OPTION_ROUTE_RULES` lives here; copying all 16 rules into
         garuda to resolve one config-driven URL would give the canonical
         URL→option mapping a second home. The trade-off is that garuda still
         serialises this nav item, so unlike the entries it strips server-side,
         this one surface is hidden client-side only. */
      const bannerOption = resolveOptionForUrl(item.id);
      if (bannerOption && !isOptionVisible(bannerOption, visibility)) {
        return null;
      }

      return {
        ...item,
        activeIconName: validIcons[item.activeIconName],
        passiveIconName: validIcons[item.passiveIconName]
      };
    }
  }
};

export const getDefaultDashboardNavItems = (
  t: TFunction,
  filterFn?: (item: DashboardNavItem) => boolean,
  visibility?: OptionVisibilityState
): DashboardNavItem[] => {
  const allItems: DashboardNavItem[] = [
    {
      id: '/dashboard/home',
      tabName: t('layout.home'),
      activeIconName: validIcons.home02Custom,
      passiveIconName: validIcons.passiveHome,
      showUnauthenticated: true
    },
    {
      id: '/dashboard/verification',
      tabName: t('layout.verification'),
      activeIconName: validIcons.checkVerified02Custom,
      passiveIconName: validIcons.passiveCheckVerified,
      showUnauthenticated: false
    },
    {
      id: '/dashboard/vault',
      tabName: t('breadCrumbs.TruLocker'),
      activeIconName: validIcons.TPIcon,
      passiveIconName: validIcons.TPIcon,
      showUnauthenticated: false
    },
    {
      id: '/dashboard/manage-account',
      tabName: t('layout.settings'),
      activeIconName: validIcons.setting01Custom,
      passiveIconName: validIcons.passiveSetting,
      showUnauthenticated: false
    },
    {
      id: '/dashboard/services',
      tabName: t('layout.services'),
      activeIconName: validIcons.grid01Custom,
      passiveIconName: validIcons.passiveGrid,
      showUnauthenticated: false
    },
    {
      id: 'support',
      tabName: t('layout.support'),
      activeIconName: validIcons.messageChatPrimary,
      passiveIconName: validIcons.messageChatNormal,
      showUnauthenticated: false
    }
  ];

  // Compose the hidden-option check with the caller's filter, so the pre-fetch
  // fallback list agrees with garuda's filtered nav response.
  return allItems.filter(
    (item) =>
      isFallbackNavOptionVisible(item.id, visibility) &&
      (!filterFn || filterFn(item))
  );
};

// extensible nav item filter
export const createNavItemFilter = (
  isAuthenticated: boolean
): ((item: DashboardNavItem) => boolean) | undefined => {
  if (!isAuthenticated) {
    return (item: DashboardNavItem) => item.showUnauthenticated === true;
  }

  // Add more conditions here as needed for future cases
  return undefined;
};

export const getOnboardingWarningMessage = (
  msg: Record<string, string> | undefined,
  lang: string
): string => {
  if (!msg) return '';
  return msg[lang] || msg[DEFAULT_LNG] || '';
};
